import {
  Tournament,
  TournamentPhase,
} from 'functions/src/types/firestore/Game/Tournament';
import {
  Team,
  Member,
} from 'functions/src/types/firestore/Game/Tournament/Guestlist';
import { PendingInvite } from './PendingInvite';
import { RegistrationDisplayType } from './Registration';

export type ParseRegistrationDataParams = Pick<
  Tournament,
  | 'prize'
  | 'maxTeamSize'
  | 'bracketType'
  | 'date'
  | 'endDate'
  | 'checkInTime'
  | 'readyTime'
  | 'title'
  | 'id'
  | 'gameId'
  | 'gameTitle'
  | 'region'
  | 'sponsors'
  | 'phase'
  | 'subscribedUserIds'
  | 'registrationOptions'
  | 'tournamentDetails'
  | 'skipCheckIn'
  | 'requirementTokens'
> & { userId: string; foundTeam: Team<Date>; teamsWithInvites: Team<Date>[] };

const getInviteUrl = (tournamentId?: string, inviteId?: string) => {
  const url = new URL(window.location.href);
  if (tournamentId && inviteId) {
    url.searchParams.append('tournamentId', tournamentId);
    url.searchParams.append('inviteId', inviteId);
  }
  return url;
};

const constructPendingInvites = (
  teamsWithInvites: Team<Date>[],
  userId: string,
  phase: TournamentPhase,
): PendingInvite[] => {
  return teamsWithInvites.map((team) => {
    const captain = team.members.find((member) => {
      return member.status === 'captain';
    }) as Member;
    return {
      teamId: team.id,
      teammates: team.members.map((member) => {
        return {
          ...member,
          isCurrentUser: member.userId === userId,
        };
      }),
      inviter: {
        username: captain?.username,
        imgUrl: captain?.imgUrl,
        userId: captain.userId,
      },
      teamName: team.name,
      isCaptain: false,
      phase: phase,
    };
  });
};

export const getUserType = async (
  userId: string,
  relevantTeams: Team<Date>[],
): Promise<RegistrationDisplayType> => {
  if (!userId) {
    return 'unregistered';
  }
  if (
    relevantTeams.find((team) => {
      return team.userIds.includes(userId) && team.isSolo;
    })
  ) {
    return 'solo';
  }

  //finding members with userId===userId
  const statuses = relevantTeams.map((team) => {
    return team.members.find((member) => {
      return member.userId === userId;
    })?.status;
  });
  const statusPriority = [
    'captain',
    'accepted',
    'pending',
    'reassigned',
    'declined',
  ];
  const parseStatus = (status?: Member['status']) => {
    if (!status) {
      return 'unregistered';
    }
    if (status === 'accepted' || status === 'captain') {
      return status;
    }
    return 'unregistered';
  };
  return parseStatus(
    statuses.sort((a, b) => {
      return (
        statusPriority.indexOf(String(a)) - statusPriority.indexOf(String(b))
      );
    })[0],
  );
};

export const parseRegistrationData = ({
  prize,
  maxTeamSize,
  bracketType,
  date,
  checkInTime,
  readyTime,
  endDate,
  title,
  id,
  gameId,
  gameTitle,
  region,
  sponsors,
  phase,
  subscribedUserIds,
  userId,
  foundTeam,
  teamsWithInvites,
  soloUsers,
  registrationOptions,
  checkInOptions, // TODO: Remove after March 1st
  tournamentDetails,
  skipCheckIn,
  requirementTokens,
}) => {
  return {
    prizePool: prize,
    maxTeamSize,
    bracketType,
    tournamentCheckInDate: checkInTime,
    tournamentStartDate: date,
    tournamentEndDate: endDate,
    tournamentName: title,
    tournamentId: id,
    tournamentReadyDate: readyTime,
    teamName: foundTeam?.name,
    teamId: foundTeam?.id,
    gameId,
    gameTitle: gameTitle,
    region,
    sponsors,
    registrationOptions: registrationOptions || checkInOptions, // TODO: Remove after March 1st
    tournamentDetails,
    phase,
    checkedIn:
      foundTeam?.members.find((member) => {
        return member.userId === userId;
      })?.checkedIn ||
      !!soloUsers?.find((solo) => {
        return solo.userId === userId && solo.checkedIn;
      }),
    // todo: update once we have subtabs routing
    inviteUrl: window ? `${getInviteUrl(id, foundTeam?.id)}` : '',
    isSubscribed: subscribedUserIds?.includes(userId || ''),
    pendingInvites: constructPendingInvites(teamsWithInvites, userId, phase),
    teammates: foundTeam?.members.map((member) => {
      return {
        ...member,
        isCurrentUser: member.userId === userId,
      };
    }),
    isWaitlisted: foundTeam?.waitlisted,
    skipCheckIn: !!skipCheckIn,
    requirementTokens,
  };
};
